import React from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import CompensationAccordion from '../../../../../components/Accordion'
import { usePayoutPopupContext } from '../context'
import { useCompensationPopupStyles } from '../styles'
import PayoutLabel from '../components/PayoutLabel'
import useAccordionLabel, { UseAccordionLabelProps } from '../components/AccordionLabel'
import CompensationContent from './CompensationContent'
import { getLabelValueWithPrefix, getLabelValueWithNegativePrefix } from '../../../utils/getLabelValue'
import AccordionIcon from '../../../../../components/Accordion/AccordionIcon'

const BonusEligibleRevenue = () => {
  const commonClasses = useCompensationPopupStyles()
  const { t } = useTranslation()
  const { data, updatePopoverPosition, isTotalBonusBiggerThanGuaranteed } = usePayoutPopupContext()

  const labelClasses: UseAccordionLabelProps['classNames'] = (isOpened) => ({
    container: clsx(commonClasses.smallSpacingLabel, {
      [commonClasses.activeAqua75Label]: isOpened && isTotalBonusBiggerThanGuaranteed,
      [commonClasses.aqua100Label]: !isOpened && isTotalBonusBiggerThanGuaranteed,
      [commonClasses.activeGrey200Label]: isOpened && !isTotalBonusBiggerThanGuaranteed,
      [commonClasses.grey300Label]: !isOpened && !isTotalBonusBiggerThanGuaranteed,
    }),
  })

  const icon = (isOpened: boolean) => <AccordionIcon className={commonClasses.smallAccordionIcon} isOpened={isOpened} />

  const renderHolidaysCompensationLabel = useAccordionLabel({
    value: getLabelValueWithPrefix(data?.publicHolidaysCompensation.absenceCompensation ?? 0),
    label: 'public_holidays_compensation',
    classNames: labelClasses,
    icon,
  })

  const renderVacationCompensationLabel = useAccordionLabel({
    value: getLabelValueWithPrefix(data?.vacationCompensation.absenceCompensation ?? 0),
    label: 'vacation_compensation',
    classNames: labelClasses,
    icon,
  })

  const renderPregnancyCompensationLabel = useAccordionLabel({
    value: getLabelValueWithPrefix(data?.pregnancyCompensation?.absenceCompensation ?? 0),
    label: 'pregnancy_compensation',
    classNames: labelClasses,
    icon,
  })

  const renderSicknessCompensationLabel = useAccordionLabel({
    value: getLabelValueWithPrefix(data?.sicknessCompensation.absenceCompensation ?? 0),
    label: 'sickness_compensation',
    classNames: labelClasses,
    icon,
  })

  if (!data) {
    return null
  }

  const commonLabelClasses = {
    label: clsx(commonClasses.labelAsAccordion, commonClasses.leftAlignedToAccordionSm),
    value: commonClasses.labelAsAccordion,
  }

  return (
    <>
      <PayoutLabel
        classes={commonLabelClasses}
        label={t('compensation_popup_monthly_revenue_label')}
        value={getLabelValueWithPrefix(data.currentMonthTreatmentRevenue)}
        format="euroInt"
      />

      <CompensationAccordion
        classes={{ opened: commonClasses.activeWhiteContent }}
        onToggleHandler={updatePopoverPosition}
        renderLabel={renderHolidaysCompensationLabel}
      >
        <CompensationContent contentType="publicHolidaysCompensation" />
      </CompensationAccordion>

      <CompensationAccordion
        classes={{ opened: commonClasses.activeWhiteContent }}
        onToggleHandler={updatePopoverPosition}
        renderLabel={renderVacationCompensationLabel}
      >
        <CompensationContent contentType="vacationCompensation" />
      </CompensationAccordion>

      {data?.pregnancyCompensation?.absenceCompensation !== 0 && (
        <CompensationAccordion
          classes={{ opened: commonClasses.activeWhiteContent }}
          onToggleHandler={updatePopoverPosition}
          renderLabel={renderPregnancyCompensationLabel}
        >
          <CompensationContent contentType="pregnancyCompensation" />
        </CompensationAccordion>
      )}

      <CompensationAccordion
        classes={{ opened: commonClasses.activeWhiteContent }}
        onToggleHandler={updatePopoverPosition}
        renderLabel={renderSicknessCompensationLabel}
      >
        <CompensationContent contentType="sicknessCompensation" />
      </CompensationAccordion>

      <PayoutLabel
        classes={commonLabelClasses}
        label={t('compensation_popup_revenue_threshold_label')}
        value={getLabelValueWithNegativePrefix(data.revenueThreshold)}
        format="euroInt"
      />
    </>
  )
}

export default BonusEligibleRevenue
